// React/Next imports
import React, { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';

import clsx from 'clsx';

// Hooks
import { useTransformStringOnOffset } from '@/utils/hooks/format/useTransformString';

// Third-party libraries
import PropTypes from 'prop-types';

// Constants/Libs
import { POST, STORY, TW_POST_TYPES } from '@/lib/posts/postTypes';
import {
    FACEBOOK,
    INSTAGRAM,
    LINKEDIN,
    PINTEREST,
    TIKTOK_BUSINESS,
    TWITTER
} from '@/lib/socialProfile';

// Components
import Icon from '@/components/base/Icon';
import { CaptionPreview } from '@/components/composite/CaptionPreview';
import { QuoteBadge } from '@/components/composite/Post/Twitter/QuoteBadge';
import { ReplyBadge } from '@/components/composite/Post/Twitter/ReplyBadge';
import { RetweetBadge } from '@/components/composite/Post/Twitter/RetweetBadge';

const MediaViewerNavigation = ({
    post,
    postType = POST,
    isLoading = false,
    socialNetworkType,
    navAction = () => {
        /** */
    },
    isNext = false,
    remoteMedia = false,
    fromCalendar = false,
    className
}) => {
    const keyNavAction = useCallback(
        (e) => {
            if (post) {
                if (
                    ((e.key === 'ArrowLeft' && !isNext) || (e.key === 'ArrowRight' && isNext)) &&
                    e.target.nodeName !== 'TEXTAREA'
                ) {
                    navAction();
                }
            }
        },
        [post, isNext, navAction]
    );

    useEffect(() => {
        document.addEventListener('keydown', keyNavAction);

        return () => {
            document.removeEventListener('keydown', keyNavAction);
        };
    }, [keyNavAction]);

    const { formatTextWithComponent, formatFacebookText, OffsetReplace } =
        useTransformStringOnOffset();

    const captionToDisplay = () => {
        switch (socialNetworkType) {
            case FACEBOOK:
                if (
                    post?.caption === '' &&
                    post?.medias?.[0]?.type === 'link' &&
                    post?.medias?.[0]?.target
                ) {
                    return post?.medias?.[0]?.target;
                }
                return formatFacebookText(post?.caption, post?.tags);
            case LINKEDIN:
            case TWITTER:
                return post?.caption_formatted;
            case PINTEREST:
                return post?.caption;
            default:
                formatTextWithComponent(post?.caption, post?.tags);
        }
    };

    const getCaptionPreview = () => {
        if (!fromCalendar) {
            return (
                <div
                    className={clsx(
                        'relative h-full w-full overflow-hidden break-words p-2 font-GMedium text-xxs',
                        { 'text-left': isNext, 'text-right': !isNext }
                    )}>
                    <OffsetReplace
                        text={captionToDisplay()}
                        components={Object.assign(
                            { p: <p />, br: <br /> },
                            post?.tags?.map((tag, id) => <span key={id} className="text-icoBlue" />)
                        )}
                    />

                    <span className="absolute bottom-0 left-0 right-0 block h-1/2 w-full bg-gradient-to-b from-icoGray-100/0 to-icoGray-100/100 dark:from-icoDarkMode-deepBlack/0 dark:to-icoDarkMode-deepBlack/100" />
                </div>
            );
        }
        return (
            <div className="relative h-full w-full overflow-hidden break-words p-2 text-left font-GMedium text-xxs">
                <CaptionPreview
                    as="span"
                    className="line-clamp-4"
                    caption={post?.data?.caption ?? ''}
                    network={socialNetworkType}
                    mentions={post?.data?.mentions ?? []}
                />
                <span className="absolute bottom-0 left-0 right-0 block h-1/2 w-full bg-gradient-to-b from-icoGray-100/0 to-icoGray-100/100 dark:from-icoDarkMode-deepBlack/0 dark:to-icoDarkMode-deepBlack/100" />
            </div>
        );
    };

    return (
        <>
            {(isLoading || post) && (
                <button
                    onClick={navAction}
                    className={clsx(
                        'fixed top-1/2 z-10 w-[6.5rem] transform overflow-hidden rounded-lg border-2 border-white bg-icoGray-100 transition-transform duration-300 dark:border-icoDarkMode-anthracite dark:bg-icoDarkMode-deepBlack',
                        {
                            '-left-20 md:-left-12': !isNext,
                            '-right-20 md:-right-12': isNext,
                            'h-20 md:h-[6.5rem]': postType !== STORY,
                            'h-20 md:h-36': postType === STORY,
                            'animate-pulse cursor-default': isLoading,
                            'cursor-pointer': !isLoading,
                            'hover:translate-x-1/4': !isLoading && !isNext,
                            'hover:-translate-x-1/4': !isLoading && isNext,
                            'p-2':
                                socialNetworkType === LINKEDIN ||
                                socialNetworkType === TWITTER ||
                                (!post?.medias?.[0]?.image &&
                                    !post?.medias?.[0]?.video &&
                                    !post?.medias?.[0]?.thumbnail)
                        },
                        className
                    )}>
                    {post?.medias?.length > 0 &&
                    (post?.medias?.[0]?.image ||
                        post?.medias?.[0]?.video ||
                        post?.medias?.[0]?.thumbnail) &&
                    socialNetworkType !== LINKEDIN ? (
                        <div className="relative h-full w-full">
                            <Img postId={post.id} remoteMedia={remoteMedia} post={post} />
                        </div>
                    ) : (
                        getCaptionPreview()
                    )}
                    {socialNetworkType === TWITTER && post?.type === TW_POST_TYPES.TYPE_RETWEET && (
                        <RetweetBadge
                            withText={false}
                            className={clsx('absolute top-1 !p-1 text-xs', {
                                'left-1': isNext,
                                'right-1': !isNext
                            })}
                        />
                    )}
                    {socialNetworkType === TWITTER && post?.type === TW_POST_TYPES.TYPE_QUOTE && (
                        <QuoteBadge
                            withText={false}
                            className={clsx('absolute top-1 !p-1 text-xs', {
                                'left-1': isNext,
                                'right-1': !isNext
                            })}
                        />
                    )}
                    {socialNetworkType === TWITTER && post?.type === TW_POST_TYPES.TYPE_REPLY && (
                        <ReplyBadge
                            withText={false}
                            className={clsx('absolute top-1 !p-1 text-xs', {
                                'left-1': isNext,
                                'right-1': !isNext
                            })}
                        />
                    )}
                </button>
            )}
        </>
    );
};

const Img = ({ postId, post, remoteMedia = false }) => {
    const [error, setError] = useState(false);

    if (post?.medias?.[0]?.video && !post?.medias?.[0]?.thumbnail) {
        if (error) {
            return (
                <div className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                    {/* Change Icon to real fallback */}
                    <Icon
                        icon="play"
                        className="text-2xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                    />
                </div>
            );
        }

        return (
            <video
                src={post?.medias?.[0]?.video}
                preload="metadata"
                className="min-h-full min-w-full"
                onError={() => setError(true)}
            />
        );
    }

    if (error || (!post?.medias?.[0]?.image && !post?.medias?.[0]?.thumbnail)) {
        return (
            <div className="flex h-full w-full items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-deepBlack">
                {/* Change Icon to real fallback */}
                <Icon
                    icon="content"
                    className="text-2xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        );
    }

    return remoteMedia ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
            src={post?.medias?.[0]?.image ?? post?.medias?.[0]?.thumbnail}
            alt={postId}
            className="h-full w-full object-cover"
            onError={() => setError(true)}
        />
    ) : (
        <Image
            src={post?.medias?.[0]?.image ?? post?.medias?.[0]?.thumbnail}
            alt={postId}
            fill
            className="h-full w-full object-cover"
            sizes="10vw"
            onError={() => setError(true)}
        />
    );
};

Img.propTypes = {
    postId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    media: PropTypes.string
};

MediaViewerNavigation.propTypes = {
    post: PropTypes.object,
    postType: PropTypes.oneOf([POST, STORY]),
    isLoading: PropTypes.bool,
    socialNetworkType: PropTypes.oneOf([FACEBOOK, INSTAGRAM, TWITTER, LINKEDIN, TIKTOK_BUSINESS]),
    navAction: PropTypes.func,
    isNext: PropTypes.bool,
    remoteMedia: PropTypes.bool,
    className: PropTypes.string,
    fromCalendar: PropTypes.bool
};

MediaViewerNavigation.Img = Img;

export default MediaViewerNavigation;
